'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Phone } from 'lucide-react';
import { ContactModal } from '@/components/contact-modal';

const faqs = [
  {
    category: '标准参编服务',
    questions: [
      {
        question: '什么是标准参编服务？',
        answer: '标准参编服务是指协助企业参与国家标准、行业标准、团体标准的制定工作。通过标准参编，企业可以提升行业话语权，树立行业权威地位，掌握行业发展方向。中科企服（湖北）科技有限公司提供从标准立项、起草、审核到发布的全流程专业服务。',
      },
      {
        question: '企业参与标准制定有什么好处？',
        answer: '企业参与标准制定的主要好处包括：1）提升行业影响力和话语权；2）树立技术权威形象；3）获取政策支持和资金补贴；4）提高企业品牌知名度；5）掌握行业发展主动权；6）增强市场竞争力。研究表明，参与标准制定的企业平均市场占有率提升15-30%。',
      },
      {
        question: '标准参编需要多长时间？',
        answer: '标准参编的周期一般为12-24个月，具体时间取决于标准类型和复杂程度。国家标准周期较长（18-24个月），行业标准中等（12-18个月），团体标准较快（6-12个月）。中科企服提供专人跟进服务，确保项目按时推进。',
      },
    ],
  },
  {
    category: '科技成果评价',
    questions: [
      {
        question: '科技成果评价有什么作用？',
        answer: '科技成果评价是对企业技术创新成果的权威认证和价值评估。主要作用包括：1）为科技成果转化提供依据；2）获得政府项目申报资格；3）申请科技奖励和荣誉；4）提升企业技术形象；5）吸引投资和融资；6）申请高新技术企业认定。',
      },
      {
        question: '科技成果评价的流程是什么？',
        answer: '科技成果评价流程包括：1）提交申请材料；2）材料初审；3）组织专家评审；4）现场考察（如需要）；5）出具评价报告；6）成果登记备案。整个流程约需15-30个工作日。中科企服提供全程指导服务。',
      },
      {
        question: '科技成果评价需要哪些材料？',
        answer: '科技成果评价所需材料包括：1）科技成果评价申请表；2）技术研究报告；3）测试报告或检测报告；4）知识产权证明材料；5）应用证明材料；6）经济效益证明；7）其他支撑材料。中科企服可协助企业准备和完善相关材料。',
      },
    ],
  },
  {
    category: '贸易增量服务',
    questions: [
      {
        question: '贸易增量服务包括哪些内容？',
        answer: '中科企服的贸易增量服务包括：1）市场趋势分析和竞争对手研究；2）客户需求洞察和市场机会挖掘；3）贸易增长策略制定；4）业务流程优化；5）数据监控和智能决策支持；6）营销推广和渠道拓展。通过数据驱动和科学方法，帮助企业实现贸易额稳步增长。',
      },
      {
        question: '贸易增量服务能带来什么效果？',
        answer: '根据中科企服的服务数据统计：平均帮助企业实现贸易额增长35%，服务企业超过200家，客户满意度达98%。通过系统化的市场分析和增长策略，企业能够拓展新市场、优化业务流程、提升运营效率，实现可持续增长。',
      },
      {
        question: '贸易增量服务的流程是怎样的？',
        answer: '贸易增量服务流程：1）现状诊断 - 全面评估企业贸易现状；2）数据分析 - 深度分析市场和业务数据；3）策略制定 - 制定科学的增长策略；4）实施推进 - 协助企业推进策略落地；5）效果评估 - 定期评估并持续优化。整个服务周期为3-12个月。',
      },
    ],
  },
  {
    category: '服务流程与费用',
    questions: [
      {
        question: '如何联系中科企服进行咨询？',
        answer: '您可以通过以下方式联系中科企服：电话咨询：187-0278-9722（李老师）；邮箱联系：781260356@qq.com；公司地址：武汉经济技术开发区车城东路10号创思汇科技大厦16层1601-43号室。专业顾问团队将为您提供一对一咨询服务。',
      },
      {
        question: '服务费用如何收取？',
        answer: '中科企服的服务费用根据具体服务类型和项目复杂程度确定。标准参编、科技成果评价、贸易增量等不同服务有不同的收费标准。建议您先电话咨询，了解具体需求后，我们会提供详细的服务方案和报价。',
      },
      {
        question: '服务周期一般多长？',
        answer: '不同服务周期不同：标准参编6-24个月；科技成果评价15-30个工作日；贸易增量服务3-12个月。我们会根据企业实际情况制定个性化服务方案，确保服务效果最大化。具体周期请咨询李老师：187-0278-9722。',
      },
    ],
  },
];

export default function FAQPage() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <nav className="text-sm mb-6 text-blue-200">
            <Link href="/" className="hover:text-white">首页</Link>
            <span className="mx-2">/</span>
            <span className="text-white">常见问题</span>
          </nav>
          <h1 className="text-4xl font-bold tracking-tight lg:text-5xl mb-4">
            常见问题解答
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl">
            关于标准参编、科技成果评价、贸易增量服务的常见问题，
            帮助您快速了解中科企服的专业服务。
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqs.map((category) => (
              <div key={category.category} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="h-8 w-1 bg-blue-600 mr-3"></span>
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`${category.category}-${index}`}
                      className="bg-white rounded-lg shadow-sm border px-6"
                    >
                      <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-blue-600 py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
            <CardHeader>
              <CardTitle className="text-2xl text-center">还有其他问题？</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-blue-100 mb-6">
                联系李老师获取一对一专业咨询服务
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  variant="default"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                  onClick={() => setIsContactOpen(true)}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  在线咨询
                </Button>
                <a href="tel:18702789722">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 w-full sm:w-auto"
                  >
                    直接拨打：187-0278-9722
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
}